import React, { useRef } from "react";
import Table from "react-bootstrap/Table";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Import images for PDF header and footer
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const AeroTable6 = ({ data }) => {
  const pdfRef = useRef();

  // Extract years and data preparation
  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Female",
        data: data.map((item) => item.Female),
        borderColor: "#FF6384",
        backgroundColor: "rgba(255, 99, 132, 0.5)",
        tension: 0.4,
        fill: true,
      },
      {
        label: "Male",
        data: data.map((item) => item.Male),
        borderColor: "#36A2EB",
        backgroundColor: "rgba(54, 162, 235, 0.5)",
        tension: 0.4,
        fill: true,
      },
      {
        label: "All Sexes",
        data: data.map((item) => item["All Sexes"]),
        borderColor: "#4BC0C0",
        backgroundColor: "rgba(75, 192, 192, 0.5)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Employment Share by Gender and Age Group',
        font: {
          size: 14,
          weight: 'bold'
        },
        padding: { top: 10, bottom: 10 }
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          font: { size: 11 },
          boxWidth: 12,
          padding: 10
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Year',
          font: {
            size: window.innerWidth < 768 ? 12 : 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true
        },
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          }
        }
      },
      y: {
        title: {
          display: true,
          text: 'Number of Employees',
          font: {
            size: window.innerWidth < 768 ? 12 : 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true
        },
        beginAtZero: true,
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 10 : 12
          }
        }
      }
    }
  };

  const downloadPDF = () => {
    const input = pdfRef.current;
    input.style.width = "1100px";

    html2canvas(input, { scale: 3 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Header
      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      // Main Content
      pdf.addImage(imgData, "PNG", 5, 38, imgWidth, imgHeight);

      // Footer
      pdf.line(10, 260, 200, 260);
      // pdf.addImage(utahData, "PNG", 33, 265, 20, 20);
      pdf.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, 275);
      pdf.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdf.text("using", 112, 275);
      pdf.addImage(kpi, "PNG", 125, 265, 20, 20);
      pdf.text(" 20250916.v3.198", 148, 275);
      const currentDate = new Date().toLocaleDateString();
      pdf.text(`Downloaded on: ${currentDate}`, 150, 295);
      const currentURL = window.location.href;
      pdf.text(`Downloaded from: ${currentURL}`, 10, 295);

      pdf.save("employment-by-age-and-gender.pdf");
      input.style.width = "";
    });
  };

  // Calculate summary metrics
  const latestData = data[data.length - 1];
  const firstData = data[0];
  
  const total2024 = latestData["All Sexes"];
  const female2024 = latestData.Female;
  const male2024 = latestData.Male;
  const femaleShare2024 = latestData["Women Share %"];
  const maleShare2024 = latestData["Men Share %"];
  
  const totalGrowth = ((total2024 - firstData["All Sexes"]) / firstData["All Sexes"] * 100).toFixed(1);
  const femaleGrowth = ((female2024 - firstData.Female) / firstData.Female * 100).toFixed(1);
  const maleGrowth = ((male2024 - firstData.Male) / firstData.Male * 100).toFixed(1);

  // Find year with highest total employment
  let maxTotal = 0;
  let peakYear = data[0].year;
  data.forEach(item => {
    if (item["All Sexes"] > maxTotal) {
      maxTotal = item["All Sexes"];
      peakYear = item.year;
    }
  });

  return (
    <div className="container">
      <div ref={pdfRef} className="content-to-export p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-lg w-full max-w-full overflow-hidden">
        <h3 className="text-xl sm:text-2xl font-bold mb-4 text-center text-gray-800">Employment by Gender (2018-2024)</h3>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4 text-center">
          <div className="p-3 bg-blue-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Total Growth Since 2018
            </p>
            <p className="text-lg font-bold text-blue-800">+{totalGrowth}%</p>
          </div>
          <div className="p-3 bg-green-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">
              Peak Employment Year
            </p>
            <p className="text-lg font-bold text-green-800">{peakYear}</p>
          </div>
          <div className="p-3 bg-purple-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">2024 Total Employment</p>
            <p className="text-lg font-bold text-purple-800">
              {total2024.toLocaleString()} 👥
            </p>
          </div>
        </div>

        {/* Gender Distribution Cards */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-center">
          <div className="p-3 bg-pink-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">Female Employment</p>
            <p className="text-lg font-bold text-pink-800">
              {female2024.toLocaleString()} ({femaleShare2024}%)
            </p>
            <p className="text-xs text-gray-500">Growth: +{femaleGrowth}%</p>
          </div>
          <div className="p-3 bg-blue-100 rounded-lg shadow-sm">
            <p className="text-xs font-medium text-gray-600 mb-1">Male Employment</p>
            <p className="text-lg font-bold text-blue-800">
              {male2024.toLocaleString()} ({maleShare2024}%)
            </p>
            <p className="text-xs text-gray-500">Growth: +{maleGrowth}%</p>
          </div>
        </div>

        {/* Table + Chart Side-by-Side Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Table */}
          <div className="w-full">
            <div className="overflow-x-auto">
              <Table striped bordered hover responsive className="w-full border-collapse text-center shadow-sm rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-sm">Year</th>
                    <th className="p-2 text-sm">All Sexes</th>
                    <th className="p-2 text-sm">Female</th>
                    <th className="p-2 text-sm">Male</th>
                    <th className="p-2 text-sm">Female %</th>
                    <th className="p-2 text-sm">Male %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="p-2 text-sm">{item.year}</td>
                      <td className="p-2 text-sm">{item["All Sexes"].toLocaleString()}</td>
                      <td className="p-2 text-sm">{item.Female.toLocaleString()}</td>
                      <td className="p-2 text-sm">{item.Male.toLocaleString()}</td>
                      <td className="p-2 text-sm">{item["Women Share %"]}%</td>
                      <td className="p-2 text-sm">{item["Men Share %"]}%</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          {/* Chart */}
          <div className="w-full">
            <div className="bg-white rounded-lg shadow-sm p-3">
              <div className="overflow-x-auto">
                <div className="relative" style={{ height: '350px', minHeight: '280px', minWidth: '400px' }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="download-btn bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg text-sm"
        >
          <span>Download as PDF</span>
          <img className="w-[1.2rem]" src={pdf} alt="PDF Icon" />
        </button>
      </div>
    </div>
  );
};

export default AeroTable6;
